import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth
export const auth = {
  login: (data) => api.post("/auth/login", data),
  me: () => api.get("/auth/me"),
};

// Users
export const usuarios = {
  list: () => api.get("/usuarios"),
  create: (data) => api.post("/auth/register", data),
  update: (id, data) => api.put(`/usuarios/${id}`, data),
  delete: (id) => api.delete(`/usuarios/${id}`),
};

// Clientes
export const clientes = {
  list: () => api.get("/clientes"),
  get: (id) => api.get(`/clientes/${id}`),
  create: (data) => api.post("/clientes", data),
  update: (id, data) => api.put(`/clientes/${id}`, data),
  delete: (id) => api.delete(`/clientes/${id}`),
};

// Fornecedores
export const fornecedores = {
  list: () => api.get("/fornecedores"),
  get: (id) => api.get(`/fornecedores/${id}`),
  create: (data) => api.post("/fornecedores", data),
  update: (id, data) => api.put(`/fornecedores/${id}`, data),
  delete: (id) => api.delete(`/fornecedores/${id}`),
};

// Produtos
export const produtos = {
  list: () => api.get("/produtos"),
  get: (id) => api.get(`/produtos/${id}`),
  create: (data) => api.post("/produtos", data),
  update: (id, data) => api.put(`/produtos/${id}`, data),
  delete: (id) => api.delete(`/produtos/${id}`),
};

// Estoque
export const estoque = {
  list: () => api.get("/estoque"),
  get: (produtoId) => api.get(`/estoque/${produtoId}`),
  update: (produtoId, data) => api.put(`/estoque/${produtoId}`, data),
};

// Concorrentes
export const concorrentes = {
  list: () => api.get("/concorrentes"),
  create: (data) => api.post("/concorrentes", data),
  delete: (id) => api.delete(`/concorrentes/${id}`),
};

// Inteligência de Mercado
export const inteligenciaMercado = {
  list: (concorrenteId) => api.get("/inteligencia-mercado", { params: { concorrente_id: concorrenteId } }),
  create: (data) => api.post("/inteligencia-mercado", data),
  update: (id, data) => api.put(`/inteligencia-mercado/${id}`, data),
  delete: (id) => api.delete(`/inteligencia-mercado/${id}`),
  uploadAnexo: (id, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/inteligencia-mercado/${id}/anexo`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  downloadAnexo: (id) => api.get(`/inteligencia-mercado/${id}/anexo`, { responseType: "blob" }),
  deleteAnexo: (id) => api.delete(`/inteligencia-mercado/${id}/anexo`),
};

// Produtos Concorrência (Legacy)
export const produtosConcorrencia = {
  list: (produtoId) => api.get("/produtos-concorrencia", { params: { produto_id: produtoId } }),
  create: (data) => api.post("/produtos-concorrencia", data),
};

// Contas Bancárias
export const contasBancarias = {
  list: () => api.get("/contas-bancarias"),
  create: (data) => api.post("/contas-bancarias", data),
  update: (id, data) => api.put(`/contas-bancarias/${id}`, data),
  delete: (id) => api.delete(`/contas-bancarias/${id}`),
};

// Movimentações Bancárias
export const movimentacoesBancarias = {
  list: (contaId) => api.get("/movimentacoes-bancarias", { params: { conta_bancaria_id: contaId } }),
  create: (data) => api.post("/movimentacoes-bancarias", data),
  delete: (id) => api.delete(`/movimentacoes-bancarias/${id}`),
};

// Notas Fiscais
export const notasFiscais = {
  list: (clienteId) => api.get("/notas-fiscais", { params: { cliente_id: clienteId } }),
  get: (id) => api.get(`/notas-fiscais/${id}`),
  create: (data) => api.post("/notas-fiscais", data),
  update: (id, data) => api.put(`/notas-fiscais/${id}`, data),
  delete: (id) => api.delete(`/notas-fiscais/${id}`),
  atualizarCustos: (id, custos) => api.put(`/notas-fiscais/${id}/custos`, custos),
  atualizarCustoTotal: (id, data) => api.put(`/notas-fiscais/${id}/custo-total`, data),
  atualizarRecebimento: (id, dados) => api.put(`/notas-fiscais/${id}/recebimento`, dados),
  upload: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/notas-fiscais/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  uploadXml: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/notas-fiscais/upload-xml", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  importarExcel: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/notas-fiscais/importar-excel", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

// Auditoria
export const auditoria = {
  list: (params) => api.get("/auditoria", { params }),
};

// Contas a Receber
export const contasReceber = {
  list: (status) => api.get("/contas-receber", { params: { status } }),
  create: (data) => api.post("/contas-receber", data),
  update: (id, data) => api.put(`/contas-receber/${id}`, data),
  delete: (id) => api.delete(`/contas-receber/${id}`),
  registrarRecebimento: (id, data) => api.post(`/contas-receber/${id}/recebimentos`, data),
  removerRecebimento: (contaId, recebimentoId) => api.delete(`/contas-receber/${contaId}/recebimentos/${recebimentoId}`),
  sincronizarNFs: () => api.post("/contas-receber/sincronizar-nfs"),
};

// Contas a Pagar
export const contasPagar = {
  list: (status) => api.get("/contas-pagar", { params: { status } }),
  create: (data) => api.post("/contas-pagar", data),
  update: (id, data) => api.put(`/contas-pagar/${id}`, data),
  delete: (id) => api.delete(`/contas-pagar/${id}`),
  uploadExtrato: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/contas-pagar/upload-extrato", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  confirmarExtrato: (itens) => api.post("/contas-pagar/confirmar-extrato", { itens }),
};

// Importações
export const importacoes = {
  list: (status) => api.get("/importacoes", { params: { status } }),
  get: (id) => api.get(`/importacoes/${id}`),
  create: (data) => api.post("/importacoes", data),
  update: (id, data) => api.put(`/importacoes/${id}`, data),
  delete: (id) => api.delete(`/importacoes/${id}`),
};

// Atividades
export const atividades = {
  list: (params) => api.get("/atividades", { params }),
  create: (data) => api.post("/atividades", data),
  update: (id, data) => api.put(`/atividades/${id}`, data),
  delete: (id) => api.delete(`/atividades/${id}`),
};

// Categorias Financeiras
export const categoriasFinanceiras = {
  list: () => api.get("/categorias-financeiras"),
  create: (data) => api.post("/categorias-financeiras", data),
};

// Centros de Custo
export const centrosCusto = {
  list: () => api.get("/centros-custo"),
  create: (data) => api.post("/centros-custo", data),
};

// Dashboard
export const dashboard = {
  financeiro: (dataInicio, dataFim) => api.get("/dashboard/financeiro", { 
    params: { data_inicio: dataInicio, data_fim: dataFim } 
  }),
  operacional: () => api.get("/dashboard/operacional"),
  portfolio: () => api.get("/dashboard/portfolio"),
  outrasEmpresas: () => api.get("/dashboard/outras-empresas"),
};

export const petronect = {
  resumo: () => api.get("/petronect/resumo"),
  grupos: () => api.get("/petronect/grupos"),
  buscar: (params) => api.get("/petronect/licitacoes", { params }),
  buscasSalvas: () => api.get("/petronect/buscas-salvas"),
  salvarBusca: (data) => api.post("/petronect/buscas-salvas", data),
  excluirBusca: (nome) => api.delete(`/petronect/buscas-salvas/${encodeURIComponent(nome)}`),
  sincronizacoes: () => api.get("/petronect/sincronizacoes"),
  baixarDocumento: (numero, ordem) => api.get(`/petronect/licitacoes/${encodeURIComponent(numero)}/documentos/${ordem}/download`, { responseType: "blob" }),
  exportar: (params) => api.get("/petronect/exportar", { params, responseType: "blob" }),
};

// Relatórios Financeiros
export const relatorios = {
  dre: (dataInicio, dataFim) => api.get("/relatorios/dre", { 
    params: { data_inicio: dataInicio, data_fim: dataFim } 
  }),
  fluxoCaixa: (meses = 3) => api.get("/relatorios/fluxo-caixa", { params: { meses } }),
  analisarExtrato: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/relatorios/analisar-extrato", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  getExtratoSalvo: () => api.get("/relatorios/extrato-salvo"),
  salvarObservacao: (index, observacoes) => api.put("/relatorios/extrato-salvo/observacao", { index, observacoes }),
  exportarExtrato: () => api.get("/relatorios/extrato-salvo/exportar", { responseType: "blob" }),
};

// Export
export const exportData = {
  clientes: () => api.get("/export/clientes", { responseType: "blob" }),
  notasFiscais: () => api.get("/export/notas-fiscais", { responseType: "blob" }),
  produtos: () => api.get("/export/produtos", { responseType: "blob" }),
  contasReceber: () => api.get("/export/contas-receber", { responseType: "blob" }),
  contasPagar: () => api.get("/export/contas-pagar", { responseType: "blob" }),
  dre: (dataInicio, dataFim) => api.get("/export/dre", { 
    params: { data_inicio: dataInicio, data_fim: dataFim }, 
    responseType: "blob" 
  }),
  fluxoCaixa: (meses) => api.get("/export/fluxo-caixa", { params: { meses }, responseType: "blob" }),
};

export const downloadExcel = async (exportFn, filename) => {
  const response = await exportFn();
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export default api;
